import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { MessageSquare, Eye, Search, Send, Lock } from "lucide-react";
import type { SupportMessageWithClient } from "@shared/schema";

interface SupportReply {
  id: string;
  supportMessageId: string;
  sender: string;
  message: string;
  createdAt: string;
}

export default function AdminSupport() {
  const { toast } = useToast();
  const [selectedMessage, setSelectedMessage] = useState<SupportMessageWithClient | null>(null);
  const [adminResponse, setAdminResponse] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");

  const { data: messages, isLoading } = useQuery<SupportMessageWithClient[]>({
    queryKey: ["/api/admin/support"],
  });

  const { data: replies } = useQuery<SupportReply[]>({
    queryKey: ["/api/admin/support", selectedMessage?.id, "replies"],
    queryFn: () => fetch(`/api/admin/support/${selectedMessage!.id}/replies`, { credentials: "include" }).then(r => r.json()),
    enabled: !!selectedMessage,
  });

  const respondMutation = useMutation({
    mutationFn: async ({ id, status, adminResponse }: { id: string; status: string; adminResponse?: string }) => {
      return apiRequest("PATCH", `/api/admin/support/${id}`, { status, adminResponse });
    },
    onSuccess: () => {
      toast({ title: "Ärende uppdaterat" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/support"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/support", selectedMessage?.id, "replies"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/support/open-count"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/support/recent-open"] });
      setAdminResponse("");
    },
    onError: () => {
      toast({ title: "Fel", description: "Kunde inte uppdatera ärendet", variant: "destructive" });
    },
  });

  const getStatusBadge = (status: string) => {
    if (status === "open") return <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-500/20">Öppen</Badge>;
    if (status === "answered") return <Badge variant="outline" className="bg-blue-500/10 text-blue-600 border-blue-500/20">Besvarad</Badge>;
    return <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/20">Stängd</Badge>;
  };

  const filteredMessages = messages?.filter((msg) => {
    if (statusFilter !== "all" && msg.status !== statusFilter) return false;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      return (
        msg.subject.toLowerCase().includes(q) ||
        msg.client?.name?.toLowerCase().includes(q) ||
        msg.message.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const openCount = messages?.filter(m => m.status === "open").length || 0;

  const openTicket = (msg: SupportMessageWithClient) => {
    setSelectedMessage(msg);
    setAdminResponse("");
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Supportärenden</h1>
        <p className="text-muted-foreground">Hantera meddelanden från kunder</p>
      </div>

      <Card>
        <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
          <div>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Ärenden
              {openCount > 0 && <Badge variant="default">{openCount} öppna</Badge>}
            </CardTitle>
            <CardDescription>Alla supportmeddelanden från kunder</CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Sök ärende..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="input-search-support"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]" data-testid="select-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alla</SelectItem>
                <SelectItem value="open">Öppna</SelectItem>
                <SelectItem value="answered">Besvarade</SelectItem>
                <SelectItem value="closed">Stängda</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : filteredMessages && filteredMessages.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Kund</TableHead>
                    <TableHead>Ämne</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Datum</TableHead>
                    <TableHead className="text-right">Visa</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredMessages.map((msg) => (
                    <TableRow key={msg.id} className="cursor-pointer" onClick={() => openTicket(msg)}>
                      <TableCell className="font-medium">{msg.client?.name || "Okänd"}</TableCell>
                      <TableCell>{msg.subject}</TableCell>
                      <TableCell>{getStatusBadge(msg.status)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {format(new Date(msg.createdAt!), "d MMM yyyy HH:mm", { locale: sv })}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={(e) => { e.stopPropagation(); openTicket(msg); }}
                          data-testid={`button-view-support-${msg.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="py-12 text-center">
              <MessageSquare className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                {searchQuery.trim() || statusFilter !== "all" ? "Inga ärenden matchar filtret" : "Inga supportärenden ännu"}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!selectedMessage} onOpenChange={() => setSelectedMessage(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div>
                <DialogTitle>{selectedMessage?.subject}</DialogTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  {selectedMessage?.client?.name} - {selectedMessage?.createdAt && format(new Date(selectedMessage.createdAt), "d MMMM yyyy HH:mm", { locale: sv })}
                </p>
              </div>
              {selectedMessage && getStatusBadge(selectedMessage.status)}
            </div>
          </DialogHeader>
          {selectedMessage && (
            <div className="flex flex-col flex-1 min-h-0">
              <div className="flex-1 overflow-y-auto space-y-3 pr-1">
                <div className="flex justify-start">
                  <div className="max-w-[85%]">
                    <div className="bg-muted rounded-lg rounded-tl-sm p-3">
                      <p className="text-sm whitespace-pre-wrap">{selectedMessage.message}</p>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-1">
                      {selectedMessage.client?.name || "Kund"} - {format(new Date(selectedMessage.createdAt!), "d MMM HH:mm", { locale: sv })}
                    </p>
                  </div>
                </div>

                {selectedMessage.adminResponse && (!replies || replies.length === 0) && (
                  <div className="flex justify-end">
                    <div className="max-w-[85%]">
                      <div className="bg-primary text-primary-foreground rounded-lg rounded-tr-sm p-3">
                        <p className="text-sm whitespace-pre-wrap">{selectedMessage.adminResponse}</p>
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-1 text-right">
                        Admin - {selectedMessage.respondedAt && format(new Date(selectedMessage.respondedAt), "d MMM HH:mm", { locale: sv })}
                      </p>
                    </div>
                  </div>
                )}

                {replies && replies.map((reply) => (
                  <div key={reply.id} className={`flex ${reply.sender === "admin" ? "justify-end" : "justify-start"}`}>
                    <div className="max-w-[85%]">
                      <div className={`rounded-lg p-3 ${
                        reply.sender === "admin"
                          ? "bg-primary text-primary-foreground rounded-tr-sm"
                          : "bg-muted rounded-tl-sm"
                      }`}>
                        <p className="text-sm whitespace-pre-wrap">{reply.message}</p>
                      </div>
                      <p className={`text-[11px] text-muted-foreground mt-1 ${reply.sender === "admin" ? "text-right" : ""}`}>
                        {reply.sender === "admin" ? "Admin" : (selectedMessage.client?.name || "Kund")} - {format(new Date(reply.createdAt), "d MMM HH:mm", { locale: sv })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {selectedMessage.status !== "closed" ? (
                <div className="mt-3 pt-3 border-t space-y-3">
                  <div className="flex items-end gap-2">
                    <Textarea
                      placeholder="Skriv ditt svar..."
                      value={adminResponse}
                      onChange={(e) => setAdminResponse(e.target.value)}
                      rows={2}
                      className="flex-1"
                      data-testid="input-admin-response"
                    />
                    <Button
                      size="icon"
                      onClick={() => respondMutation.mutate({ id: selectedMessage.id, status: "answered", adminResponse })}
                      disabled={respondMutation.isPending || !adminResponse.trim()}
                      data-testid="button-send-response"
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      respondMutation.mutate({ id: selectedMessage.id, status: "closed", adminResponse: adminResponse || undefined });
                      setSelectedMessage(null);
                    }}
                    disabled={respondMutation.isPending}
                    data-testid="button-close-ticket"
                  >
                    <Lock className="h-4 w-4 mr-2" />
                    Stäng ärende
                  </Button>
                </div>
              ) : (
                <div className="mt-3 pt-3 border-t">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Lock className="h-4 w-4" />
                      <span>Ärendet är stängt</span>
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => respondMutation.mutate({ id: selectedMessage.id, status: "open" })}
                      disabled={respondMutation.isPending}
                      data-testid="button-reopen-ticket"
                    >
                      Öppna igen
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
